import React, { useState } from "react";
import { api, fileToBase64, arabicError } from "../lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, Image as ImageIcon, BookmarkPlus, MessageCircle, Sparkles, Camera, FileSearch, MapPin, Crop } from "lucide-react";
import ReservationModal from "../components/ReservationModal";
import QuadCropperModal from "../components/QuadCropperModal";
import MerchantPhotoThumb from "../components/MerchantPhotoThumb";
import StarRating from "../components/StarRating";
import { PRODUCT_TYPES, PRODUCT_CATEGORIES, COUNTRIES } from "../lib/constants";

const ResultCard = ({ p, onReserve }) => {
  const outOfStock = p.status === "out_of_stock" || (p.quantity_available ?? 0) <= 0;
  const sim = Math.round((p.similarity || 0) * 100);
  const address = [p.merchant_city, p.merchant_address].filter(Boolean).join(" — ");
  const gmapsLink = p.merchant_location ? `https://www.google.com/maps?q=${p.merchant_location.lat},${p.merchant_location.lng}` : null;
  return (
    <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden" data-testid={`result-${p.id}`}>
      <div className="relative">
        <div className="w-full h-48">
          <MerchantPhotoThumb photoId={p.id} testid={`result-thumb-${p.id}`} />
        </div>
        {p.is_exact && (
          <span className="absolute top-2 left-2 bg-green-600 text-white text-xs font-extrabold px-2 py-1 rounded">مطابق 100%</span>
        )}
        {!p.is_exact && p.similarity != null && (
          <span className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-1 rounded ${p.match_strength === "strong" ? "bg-[#09637E]" : "bg-[#7AB2B2]"}`} data-testid={`result-strength-${p.id}`}>
            {p.match_strength === "strong" ? "تشابه قوي" : "مشابه"} {sim}%
          </span>
        )}
        {outOfStock && (
          <span className="absolute top-2 right-2 bg-gray-700 text-white text-xs font-extrabold px-2 py-1 rounded">نفد المخزون</span>
        )}
      </div>
      <div className="p-4 space-y-1">
        {p.product_code && (
          <div className="text-xs font-bold text-[#09637E] bg-[#EBF4F6] rounded px-2 py-1 w-fit" data-testid={`result-code-${p.id}`}>
            كود المنتج: <span dir="ltr" className="font-mono">{p.product_code}</span>
          </div>
        )}
        <div className="font-bold text-[#333333] flex items-center gap-2 flex-wrap">
          <span>{p.merchant_name}</span>
          <StarRating avg={p.merchant_rating_avg} count={p.merchant_rating_count} size={12} testid={`merchant-rating-${p.id}`} />
        </div>
        {address && (
          <div className="text-xs text-[#666666] flex items-start gap-1">
            <MapPin size={12} className="mt-0.5 shrink-0" />
            <span className="flex-1">
              {address}
              {gmapsLink && (
                <a href={gmapsLink} target="_blank" rel="noopener noreferrer" className="text-[#088395] font-bold mr-1 hover:underline">
                  · فتح على الخريطة
                </a>
              )}
            </span>
          </div>
        )}
        <div className="text-sm text-[#666666]">{p.size} · {p.color || "—"}</div>
        <div className="text-sm text-[#666666]">{p.category} · {p.country}</div>
        <div className="text-xl font-extrabold text-[#09637E]">${(p.price_usd ?? p.price ?? 0).toFixed(2)} / م</div>
        <div className="text-xs text-[#666666]">المتوفر: {p.quantity_available ?? p.quantity ?? 0} م</div>
        <button onClick={() => onReserve(p)} disabled={outOfStock} data-testid={`reserve-${p.id}`}
          className="w-full mt-2 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed">
          <BookmarkPlus size={16} /> {outOfStock ? "نفد" : "احجز"}
        </button>
      </div>
    </div>
  );
};

const Search = () => {
  const [mode, setMode] = useState("image"); // "image" | "info"
  const [preview, setPreview] = useState(null);
  const [rawImage, setRawImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [filters, setFilters] = useState({ category: "", type: "", country: "" });
  const [infoForm, setInfoForm] = useState({ type: PRODUCT_TYPES[0], category: PRODUCT_CATEGORIES[0], country: COUNTRIES[0], size: "", color: "", manufacturer: "" });
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [reserveOn, setReserveOn] = useState(null);

  const onFile = async (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) { toast.error("JPG أو PNG فقط"); return; }
    const raw = await fileToBase64(f);
    setRawImage(raw);
    setPreview(raw);
    setResults(null);
  };

  const imageSearch = async () => {
    if (!preview) { toast.error("اختر صورة أولاً"); return; }
    setSearching(true);
    try {
      const body = { image_base64: preview };
      if (filters.category) body.category = filters.category;
      if (filters.type) body.type = filters.type;
      if (filters.country) body.country = filters.country;
      const r = await api.post("/photos/search", body);
      setResults(r.data);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSearching(false); }
  };

  const infoSearch = async (e) => {
    e?.preventDefault();
    if (!infoForm.size.trim()) { toast.error("المقاس مطلوب"); return; }
    setSearching(true);
    try {
      const r = await api.post("/photos/search-by-info", {
        type: infoForm.type, category: infoForm.category, country: infoForm.country,
        size: infoForm.size.trim(),
        color: infoForm.color.trim() || null,
        manufacturer: infoForm.manufacturer.trim() || null,
      });
      setResults(r.data);
    } catch (e) { toast.error(arabicError(e)); }
    finally { setSearching(false); }
  };

  const inputCls = "h-12 w-full rounded-lg border border-[#7AB2B2] bg-white px-4 focus:outline-none focus:ring-2 focus:ring-[#09637E]";
  const selectCls = "h-11 rounded-lg border border-[#7AB2B2] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="search-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#333333]">البحث عن منتج</h1>
        <p className="text-[#666666] mt-1">اختر طريقة البحث المناسبة</p>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="search-mode-tabs">
        <button onClick={() => { setMode("image"); setResults(null); }} data-testid="mode-image"
          className={`h-14 rounded-xl border-2 font-bold flex items-center justify-center gap-2 transition-colors ${mode === "image" ? "bg-[#09637E] text-white border-[#09637E]" : "bg-white text-[#09637E] border-[#7AB2B2]"}`}>
          <Camera size={18} /> البحث بالصورة
        </button>
        <button onClick={() => { setMode("info"); setResults(null); }} data-testid="mode-info"
          className={`h-14 rounded-xl border-2 font-bold flex items-center justify-center gap-2 transition-colors ${mode === "info" ? "bg-[#09637E] text-white border-[#09637E]" : "bg-white text-[#09637E] border-[#7AB2B2]"}`}>
          <FileSearch size={18} /> البحث بالمعلومات
        </button>
      </div>

      {mode === "image" && (
        <div className="bg-white border border-[#7AB2B2] rounded-2xl p-6 space-y-5">
          <label htmlFor="search-file" className="block cursor-pointer">
            <div className="border-2 border-dashed border-[#7AB2B2] rounded-xl p-6 hover:border-[#09637E] hover:bg-[#EBF4F6] transition-colors">
              {preview ? (
                <div className="flex items-center gap-4">
                  <img src={preview} alt="" className="w-32 h-32 rounded-lg object-contain bg-[#EBF4F6] shrink-0" data-testid="search-preview" />
                  <div className="min-w-0">
                    <div className="font-bold text-[#333333]">الصورة جاهزة</div>
                    <div className="text-sm text-[#666666]">اضغط لتغيير الصورة</div>
                    <div className="text-xs text-[#666666] mt-1" data-testid="search-image-mode">
                      {preview !== rawImage ? "الصورة مقصوصة" : "الصورة الأصلية بدون قصّ"}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button type="button" onClick={(e) => { e.preventDefault(); setShowCropper(true); }} data-testid="search-crop-btn"
                        className="h-9 px-3 rounded-lg border-2 border-[#088395] text-[#088395] font-bold text-xs flex items-center gap-1 hover:bg-[#EBF4F6]">
                        <Crop size={14} /> {preview !== rawImage ? "إعادة القصّ" : "قصّ الصورة (اختياري)"}
                      </button>
                      {preview !== rawImage && (
                        <button type="button" onClick={(e) => { e.preventDefault(); setPreview(rawImage); setResults(null); }} data-testid="search-use-original-btn"
                          className="h-9 px-3 rounded-lg border-2 border-[#7AB2B2] text-[#666666] font-bold text-xs hover:bg-[#EBF4F6]">
                          استخدام الأصلية
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-8">
                  <Sparkles className="text-[#09637E] mb-3" size={48} />
                  <div className="font-bold text-[#333333]">ارفع صورة البلاط المطلوب</div>
                </div>
              )}
            </div>
          </label>
          <input id="search-file" type="file" accept="image/jpeg,image/png"
            className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="search-file-input" />
          <input id="search-camera" type="file" accept="image/*" capture="environment"
            className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="search-camera-input" />
          <div className="grid grid-cols-2 gap-2 md:hidden" data-testid="search-mobile-source">
            <button type="button" onClick={() => document.getElementById("search-camera")?.click()} data-testid="search-capture-btn"
              className="h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white text-sm font-bold flex items-center justify-center gap-1">
              <Camera size={16} /> التقاط صورة
            </button>
            <button type="button" onClick={() => document.getElementById("search-file")?.click()} data-testid="search-gallery-btn"
              className="h-11 rounded-lg border-2 border-[#09637E] text-[#09637E] text-sm font-bold hover:bg-[#EBF4F6] flex items-center justify-center gap-1">
              <ImageIcon size={16} /> اختيار من المعرض
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select className={selectCls} value={filters.category} onChange={(e) => setFilters((s) => ({ ...s, category: e.target.value }))} data-testid="search-filter-category">
              <option value="">كل الأصناف</option>
              {PRODUCT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className={selectCls} value={filters.type} onChange={(e) => setFilters((s) => ({ ...s, type: e.target.value }))} data-testid="search-filter-type">
              <option value="">كل الأنواع</option>
              {PRODUCT_TYPES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select className={selectCls} value={filters.country} onChange={(e) => setFilters((s) => ({ ...s, country: e.target.value }))} data-testid="search-filter-country">
              <option value="">كل البلدان</option>
              {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>

          <p className="text-center text-[#09637E] font-bold text-sm">ابحث بالصورة. إمداد تجد لك ما ينقصك خلال ثوانٍ.</p>

          <button onClick={imageSearch} disabled={searching || !preview} data-testid="search-submit"
            className="h-12 w-full md:w-auto px-8 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
            <SearchIcon size={18} /> {searching ? "جارٍ البحث..." : "ابحث عن المشابه"}
          </button>
        </div>
      )}

      {mode === "info" && (
        <form onSubmit={infoSearch} className="bg-white border border-[#7AB2B2] rounded-2xl p-6 space-y-4" data-testid="info-search-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">نوع المنتج *</label>
              <select className={inputCls} value={infoForm.type} onChange={(e) => setInfoForm((s) => ({ ...s, type: e.target.value }))} data-testid="info-type">
                {PRODUCT_TYPES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">الصنف *</label>
              <select className={inputCls} value={infoForm.category} onChange={(e) => setInfoForm((s) => ({ ...s, category: e.target.value }))} data-testid="info-category">
                {PRODUCT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">بلد المنشأ *</label>
              <select className={inputCls} value={infoForm.country} onChange={(e) => setInfoForm((s) => ({ ...s, country: e.target.value }))} data-testid="info-country">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">المقاس *</label>
              <input required value={infoForm.size} onChange={(e) => setInfoForm((s) => ({ ...s, size: e.target.value }))} placeholder="40x40" data-testid="info-size" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">اللون</label>
              <input value={infoForm.color} onChange={(e) => setInfoForm((s) => ({ ...s, color: e.target.value }))} placeholder="بيج" data-testid="info-color" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">الشركة المصنعة</label>
              <input value={infoForm.manufacturer} onChange={(e) => setInfoForm((s) => ({ ...s, manufacturer: e.target.value }))} placeholder="RAK" data-testid="info-manufacturer" className={inputCls} />
            </div>
          </div>
          <button type="submit" disabled={searching} data-testid="info-search-submit"
            className="h-12 px-8 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-60">
            <SearchIcon size={18} /> {searching ? "..." : "بحث"}
          </button>
        </form>
      )}

      {results !== null && (
        <section>
          <h2 className="text-2xl font-extrabold text-[#333333] mb-4">النتائج ({results.length})</h2>
          {results.length === 0 ? (
            <div className="bg-white border border-[#7AB2B2] rounded-xl p-12 text-center" data-testid="search-empty">
              <ImageIcon className="mx-auto text-[#7AB2B2] mb-3" size={48} />
              <p className="text-[#666666] text-lg font-bold" data-testid="search-no-match-msg">لم يتم العثور على منتج مطابق بدرجة كافية</p>
              <p className="text-sm text-[#666666] mt-2">نعرض فقط المنتجات المتشابهة فعلياً. جرّب قصّ الصورة على المنتج فقط، أو تصويره من الأعلى بإضاءة جيدة، أو تغيير الفلاتر</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="search-results">
              {results.map((p) => <ResultCard key={p.id} p={p} onReserve={setReserveOn} />)}
            </div>
          )}
        </section>
      )}

      {reserveOn && (
        <ReservationModal product={reserveOn} onClose={() => setReserveOn(null)} />
      )}
      {showCropper && rawImage && (
        <QuadCropperModal imageSrc={rawImage} onCancel={() => setShowCropper(false)}
          onConfirm={(dataUrl) => { setPreview(dataUrl); setResults(null); setShowCropper(false); }} />
      )}
    </div>
  );
};

export default Search;
